import { useState } from 'react';

import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';

import { useGetUserActivity, UserActivityItem } from 'api/hooks/private/users/GetUserActivity';

dayjs.extend(calendar);
dayjs.extend(advancedFormat);

const columns: GridColDef<UserActivityItem>[] = [
  {
    field: 'description',
    headerName: 'Activity Description',
    type: 'string',
    sortable: false,
    width: 500,
  },
  {
    field: 'timestamp',
    headerName: 'Time',
    type: 'dateTime',
    sortable: true,
    width: 250,
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).calendar(null, {
          sameElse: 'MMM D, YYYY [at] h:mm a',
        });
      }
      return null;
    },
  },
  {
    field: 'ip_address',
    headerName: 'IP Address',
    type: 'string',
    sortable: false,
    width: 250,
  },
  {
    field: 'user_agent',
    headerName: 'User Agent',
    type: 'string',
    sortable: false,
    flex: 1,
  },
];

interface UserActivityProps {
  userId: string;
}

export function UserActivity({ userId: id }: UserActivityProps) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0,
  });

  const userActivity = useGetUserActivity({
    variables: {
      id: id ?? '',
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    },
    // use previous data as placeholder to maintain a stable
    // reference while fetching the next page so the datagrid
    // doesn't reset the pagination model
    placeholderData: keepPreviousData,
    enabled: !!id,
  });

  return (
    <DataGrid<UserActivityItem>
      loading={userActivity.isFetching}
      columns={columns}
      rows={userActivity.data?.rows}
      paginationMode="server"
      rowCount={userActivity.data?.count ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      disableColumnFilter
      disableColumnSorting
      sx={{
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none',
        },
        '& .MuiInputBase-root': {
          width: 'auto!important',
        },
      }}
    />
  );
}

export default UserActivity;
